import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { UserContext } from '../../context/UserContext';
import FileUpload from '../../features/trainings/components/FileUpload';
import InputWithIcon from '../../components/InputWithIcon';
import DatePicker from '../../components/DatePicker';
import UploadErrors from '../../components/UploadErrors';
import { formatFileSize } from '../../utils/file-validation';
import { dialogActionsStyle, primaryButtonStyle, secondaryButtonStyle } from '../../utils/sharedStyles';
import protocolIcon from '../../assets/protocols/protocol.svg';
import authorIcon from '../../assets/protocols/author.svg';
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_FILES, DOCUMENT_MAX_SIZE } from './documentTypes';
import { uploadDocuments } from './departmentDocumentsApi';
import FolderSelect from './FolderSelect';
import { todayForPicker, toServerDate } from './documentDate';

const nameWithoutExtension = (fileName = '') =>
  fileName.includes('.') ? fileName.split('.').slice(0, -1).join('.') : fileName;

// Upload one or more files into a folder. Each file becomes its own document.
// Below the picker: the document's name (one file only; with several, each is
// named in the picker), its date and its author — all optional, defaulting to
// the file name, today and the current user.
const UploadDocumentsDialog = ({ open, departmentId, folderOptions, initialFolder, onUploaded, onClose }) => {
  const { user } = useContext(UserContext);
  const [files, setFiles] = useState([]);
  const [folder, setFolder] = useState(initialFolder ?? null);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [author, setAuthor] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setFolder(initialFolder ?? null);
      setName('');
      setDate(todayForPicker());
      setAuthor(user?.name || '');
      setError('');
    }
  }, [open, initialFolder, user?.name]);

  const single = files.length === 1;

  const handleFilesChange = (newFiles) => {
    // Whenever the selection becomes a single file, the name field starts from
    // that file's name (or whatever it was renamed to in the picker).
    if (newFiles.length === 1 && (files.length !== 1 || newFiles[0] !== files[0])) {
      setName(newFiles[0].displayName || nameWithoutExtension(newFiles[0].name));
    }
    setFiles(newFiles);
    setError('');
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('יש לבחור לפחות קובץ אחד');
      return;
    }
    if (single) files[0].displayName = name.trim() || nameWithoutExtension(files[0].name);
    setUploading(true);
    setError('');
    try {
      const created = await uploadDocuments({
        department: departmentId,
        folder,
        files,
        documentDate: toServerDate(date) || undefined,
        author: author.trim() || undefined,
      });
      onUploaded(created);
    } catch (err) {
      setError(err?.serverError?.hebMessage || 'העלאת הקבצים נכשלה. אנא נסה שנית.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>העלאת קבצים</DialogTitle>
      <DialogContent>
        <div className="dd-dialog-fields">
          {folderOptions.length > 1 && (
            <FolderSelect options={folderOptions} value={folder} onChange={setFolder} />
          )}
          <FileUpload
            label={null}
            onFilesChange={handleFilesChange}
            initialFiles={files}
            maxFiles={DOCUMENT_MAX_FILES}
            maxSize={DOCUMENT_MAX_SIZE}
            accept={DOCUMENT_ACCEPT}
            dropzoneSubtext={`ניתן להעלות מסמכים ותמונות עד ${formatFileSize(DOCUMENT_MAX_SIZE)} לקובץ`}
            // With one file its name is the field below; with several, each is
            // named in the picker.
            enableFileNameEdit={!single}
          />
          {single && (
            <InputWithIcon
              icon={protocolIcon}
              placeholder="שם המסמך"
              aria-label="שם המסמך"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          <DatePicker placeholder="תאריך המסמך - DD/MM/YYYY" value={date} onChange={setDate} />
          <InputWithIcon
            icon={authorIcon}
            placeholder="נכתב על ידי"
            aria-label="נכתב על ידי"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
          />
        </div>
        <UploadErrors messages={error ? [error] : []} onDismiss={() => setError('')} autoDismissMs={0} />
      </DialogContent>
      <DialogActions style={dialogActionsStyle}>
        <Button style={secondaryButtonStyle} onClick={onClose} disabled={uploading}>ביטול</Button>
        <Button style={primaryButtonStyle} onClick={handleUpload} disabled={uploading}>
          {uploading ? 'מעלה...' : 'העלאה'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UploadDocumentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  departmentId: PropTypes.string,
  folderOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
  initialFolder: PropTypes.string,
  onUploaded: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default UploadDocumentsDialog;
